// STATYSTYKA — ODCYH. STANDARDOWE (dla całej populacji, dzielimy przez n), bez Σ w wyjaśnieniu
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { const t = a % b; a = b; b = t; } return a || 1; }
function simplify(p, q) { if (q < 0) { p = -p; q = -q; } const g = gcd(p, q); return [p / g, q / g]; }
function dfrac(p, q) { const [pp, qq] = simplify(p, q); return qq === 1 ? `${pp}` : `\\dfrac{${pp}}{${qq}}`; }
function parseFraction(raw) {
  if (!raw) return null; const s0 = String(raw).trim(); if (!s0) return null;
  let m = s0.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/); if (m) { const den = parseInt(m[2],10); if (!den) return null; return [parseInt(m[1],10), den]; }
  if (/^[+-]?\d+$/.test(s0)) return [parseInt(s0,10),1];
  const s = s0.replace(",", "."); if (/^[+-]?(?:\d+|\d*\.\d+)$/.test(s)) {
    const sign = s.startsWith("-")?-1:1; const body = s.replace(/^[+-]/,""); const [ip,fp=""]=body.split(".");
    const k = fp.length; const p = sign * (parseInt(ip||"0",10)*(10**k) + parseInt(fp||"0",10)); const q = 10**k; return [p,q];
  }
  return null;
}
function sq(x){ return x*x; }
function sum(a){ return a.reduce((u,v)=>u+v,0); }
function shuffle(a){ for(let i=a.length-1;i>0;i--){ const j=Math.floor(Math.random()*(i+1)); [a[i],a[j]]=[a[j],a[i]]; } return a; }

// ...nad tym masz: gcd, simplify, dfrac

function mixedLatex(p, q) {
  let [pp, qq] = simplify(p, q);
  const neg = pp < 0;
  pp = Math.abs(pp);
  const whole = Math.floor(pp / qq);
  const rem = pp % qq;
  if (whole === 0 && rem === 0) return "0";
  if (whole === 0) return (neg ? "-" : "") + `\\dfrac{${rem}}{${qq}}`;
  if (rem === 0) return (neg ? "-" : "") + `${whole}`;
  return (neg ? "-" : "") + `${whole}\\; ${dfrac(rem, qq)}`;
}

// ========== GENERATOR ==========
export function generateTask() {
  const n = 5 + Math.floor(Math.random()*3); // 5..7
  const data = Array.from({length:n}, () => 2 + Math.floor(Math.random()*11)); // 2..12
  const total = sum(data);
  const meanP = total; const meanQ = n;
  const [mp, mq] = simplify(meanP, meanQ); // średnia jako ułamek mp/mq

  const mean = total / n;
  const sqDevs = data.map(x => sq(x - mean));
  const varVal = sum(sqDevs) / n;
  const stdev = Math.sqrt(varVal);

  return {
    type: "StdDeviationTask",
    data: { n, data, total, mean, mp, mq, sqDevs, varVal, stdev },
    uid: `stdev_${Date.now()}_${Math.floor(Math.random()*1e9)}`
  };
}

// ========== TREŚĆ ==========
export function renderTask({ data }) {
  const list = shuffle([...data.data]);
  const listLatex = list.join(",\\,");
  return (
    <div style={{textAlign:'left', lineHeight:1.6}}>
      <div>Podane dane: <InlineMath math={listLatex} /><span>.</span></div>
      <div style={{marginTop:4}}>
        Oblicz <b>odchylenie standardowe</b> <InlineMath math={'s'} /> (dla całej populacji, dzielimy przez <InlineMath math={'n'} />).
      </div>
    </div>
  );
}

// ========== INPUT ==========
export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-3" style={{marginTop:6}}>
      <span><InlineMath math={'s = '} /></span>
      <FractionInput value={userAnswer} onChange={onChange} disabled={disabled} label="s" placeholder="np. 2.35" />
    </div>
  );
}

// ========== CHECK ==========
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || !userAnswer.raw) return { correct:false, message:"Podaj odchylenie standardowe." };
  const parsed = parseFraction(userAnswer.raw); if (!parsed) return { correct:false, message:"Nieprawidłowy zapis." };
  const uv = parsed[0]/parsed[1]; const correct = Math.abs(uv - data.stdev) <= 0.01 + 1e-12;
  const approx = (Math.round(data.stdev * 100)/100).toFixed(2);
  return { correct, expected: `s \\approx ${approx}` };
}

// ========== ODPOWIEDŹ / PODGLĄD ==========
export function renderAnswer({ data }) {
  const approx = (Math.round(data.stdev * 100)/100).toFixed(2);
  return <InlineMath math={`s \\approx ${approx}`} />;
}
export function renderUserAnswer(ans) {
  if (!ans || !ans.raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={formatLatexPreview(ans.raw, 's')} />;
}

// ========== WYJAŚNIENIE (bez Σ) ==========
export function renderExplanation({ data }) {
  const n = data.n, xs = data.data;
  const numSum = xs.join(" + ");
  const meanImp = dfrac(data.total, n);
  const meanMixed = (() => {
    const [pp, qq] = simplify(data.total, n);
    return qq===1 ? `${pp}` : mixedLatex(pp, qq);
  })();

  const devTerms = xs.map(x => `(${x} - ${meanImp})^2`).join(" + ");
  const devNums  = xs.map(x => `(${(x - data.mean).toFixed(2)})^2`).join(" + ");
  const sqDevsSum = data.sqDevs.reduce((a,b)=>a+b,0);

  const varApprox = (Math.round(data.varVal*10000)/10000).toFixed(4);
  const sApprox = (Math.round(data.stdev*100)/100).toFixed(2);

  return (
    <div style={{textAlign:'left', lineHeight:1.75}}>
      <div><strong>Krok 1 — średnia arytmetyczna</strong></div>
      <BlockMath math={`\\bar{x} = \\dfrac{${numSum}}{${n}} = ${meanImp} \\; (\\approx\\; ${meanMixed})`} />

      <div style={{marginTop:8}}><strong>Krok 2 — wariancja</strong></div>
      <BlockMath math={`s^2 = \\dfrac{(x_1 - \\bar{x})^2 + \\dots + (x_{${n}} - \\bar{x})^2}{${n}}`} />
      <BlockMath math={`s^2 = \\dfrac{${devTerms}}{${n}}`} />
      <BlockMath math={`s^2 \\approx \\dfrac{${devNums}}{${n}} = ${varApprox}`} />

      <div style={{marginTop:8}}><strong>Krok 3 — odchylenie standardowe</strong></div>
      <BlockMath math={`s = \\sqrt{s^2} \\approx \\sqrt{${varApprox}} \\approx ${sApprox}`} />
    </div>
  );
}
